import React, { useState } from "react";
import { Box } from "@mui/material";
import { NavLink } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import API_BASE_URL from "../../components/config/appConfig";
import useReveal from "./useReveal";
import PageHero from "./PageHero";
import { CONTACT } from "./siteContent";
import { IconCheck, IconArrow, IconUsers, IconShield } from "../Home/landingArt";
import {
  IconMail,
  IconPhone,
  IconPin,
  IconClock,
  IconLifebuoy,
} from "./marketingArt";
import "../Home/landingpage.scss";
import "./marketing.scss";

const TEAM_SIZES = [
  "Just me",
  "2 – 10 people",
  "11 – 50 people",
  "51 – 200 people",
  "201 – 500 people",
  "More than 500 people",
];

const TOPICS = [
  "Choosing a plan",
  "A demo for my team",
  "Migrating from another tool",
  "Billing or invoicing",
  "Technical support",
  "Partnership or press",
  "Something else",
];

const contactSchema = Yup.object().shape({
  name: Yup.string().trim().required("Please tell us your name"),
  email: Yup.string()
    .trim()
    .email("That does not look like a valid email address")
    .required("We need an email address to reply to"),
  company: Yup.string().trim().required("Please tell us your company"),
  teamSize: Yup.string().required("Please choose a team size"),
  topic: Yup.string().required("Please choose a topic"),
  message: Yup.string()
    .trim()
    .min(20, "A little more detail helps us give you a useful answer")
    .required("Please tell us what you need"),
});

/**
 * Only used by the fallback below: the server decides where a real enquiry
 * goes, and the admin address deliberately stays out of the public bundle.
 */
const routeFor = (topic) => {
  if (topic === "Technical support") return CONTACT.support;
  if (topic === "Partnership or press" || topic === "Something else") {
    return CONTACT.general;
  }
  return CONTACT.sales;
};

const mailtoFor = (values) => {
  const body = [
    `Name: ${values.name}`,
    `Email: ${values.email}`,
    `Company: ${values.company}`,
    `Team size: ${values.teamSize}`,
    `Topic: ${values.topic}`,
    "",
    values.message,
  ].join("\n");

  return `mailto:${routeFor(values.topic)}?subject=${encodeURIComponent(
    `${values.topic} — ${values.company}`
  )}&body=${encodeURIComponent(body)}`;
};

const CHANNELS = [
  {
    icon: <IconMail />,
    tone: "is-green",
    title: "Sales",
    body: "Plans, demos, migrating an existing backlog, or a proposal for a larger organisation.",
    label: CONTACT.sales,
    href: `mailto:${CONTACT.sales}`,
  },
  {
    icon: <IconLifebuoy />,
    tone: "is-blue",
    title: "Support",
    body: "Something not behaving as it should, or a question about how a part of Projam works.",
    label: CONTACT.support,
    href: `mailto:${CONTACT.support}`,
  },
  {
    icon: <IconPhone />,
    tone: "is-violet",
    title: "Phone",
    body: CONTACT.hours,
    label: CONTACT.phone,
    href: `tel:${CONTACT.phoneHref}`,
  },
  {
    icon: <IconPin />,
    tone: "is-amber",
    title: "Where we are",
    body: CONTACT.addressLines.join(" · "),
  },
];

const REASSURANCE = [
  {
    icon: <IconClock />,
    tone: "is-green",
    title: "A real reply, quickly",
    body: `Messages are answered ${CONTACT.responseTime} by someone who works on Projam — not an autoresponder.`,
  },
  {
    icon: <IconUsers />,
    tone: "is-blue",
    title: "A demo on your own work",
    body: "We would rather walk through your backlog than a sample project. Bring a real sprint and we will set it up.",
  },
  {
    icon: <IconShield />,
    tone: "is-violet",
    title: "No hard sell",
    body: "If Projam is not the right fit for how your team works, we will say so. It saves everyone a quarter.",
  },
];

const Contact = () => {
  const rootRef = useReveal();
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState(null);

  /**
   * Posts the enquiry to the API, which emails it on to whoever handles sales.
   *
   * If that call fails the enquiry is not dropped on the floor: the visitor is
   * told plainly, their answers are left in the form, and they are offered a
   * pre-composed email as a way through.
   */
  const handleSubmit = async (values, { setSubmitting }) => {
    setFailure(null);

    try {
      await axios.post(`${API_BASE_URL}/contact/enquiry`, {
        name: values.name,
        email: values.email,
        company: values.company,
        teamSize: values.teamSize,
        topic: values.topic,
        message: values.message,
        /* Honeypot — hidden from people, so anything in it came from a bot. */
        website: values.website,
      });

      setSent(true);
    } catch (error) {
      const status = error?.response?.status;
      setFailure({
        message:
          error?.response?.data?.message ||
          "We could not send your message just now.",
        /* A throttled visitor is not stuck — they simply sent several already. */
        retryable: status !== 429,
        mailto: mailtoFor(values),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className="pj-landing pj-page" ref={rootRef}>
      <PageHero
        eyebrow="Contact us"
        title={
          <>
            Tell us what your team needs{" "}
            <span className="lp-gradient-text">and we will help</span>
          </>
        }
        lead={`Whether you are comparing plans, want a walkthrough on your own backlog, or have hit something that is not working — write to us and you will hear back ${CONTACT.responseTime}.`}
      />

      {/* -------------------------------------------------- form + channels */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="pg-contact">
            {/* ------------------------------------------------------ form */}
            <div className="pg-form-card" data-reveal>
              {sent ? (
                <div className="pg-sent">
                  <div className="pg-sent-mark">
                    <IconCheck />
                  </div>
                  <h3 className="lp-h3">Thanks — your message is on its way</h3>
                  <p>
                    It has reached the team, and someone who works on Projam
                    will reply {CONTACT.responseTime}. Keep an eye on the inbox
                    you gave us.
                  </p>
                  <div className="pg-sent-actions">
                    <button
                      type="button"
                      className="lp-btn lp-btn--ghost"
                      onClick={() => setSent(false)}
                    >
                      Write another message
                    </button>
                    <NavLink to="/pricing" className="lp-btn lp-btn--primary">
                      See pricing <IconArrow />
                    </NavLink>
                  </div>
                </div>
              ) : (
                <>
                  <span className="lp-eyebrow">Send a message</span>
                  <h2
                    className="lp-h2"
                    style={{ fontSize: 26, marginBottom: 22 }}
                  >
                    Get in touch
                  </h2>

                  <Formik
                    initialValues={{
                      name: "",
                      email: "",
                      company: "",
                      teamSize: "",
                      topic: "",
                      message: "",
                      /* honeypot — see handleSubmit */
                      website: "",
                    }}
                    validationSchema={contactSchema}
                    onSubmit={handleSubmit}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      isSubmitting,
                    }) => {
                      const fieldClass = (key) =>
                        `pg-field${
                          touched[key] && errors[key] ? " has-error" : ""
                        }`;

                      return (
                        <Form noValidate>
                          {failure ? (
                            <div className="pg-form-alert" role="alert">
                              <p>
                                {failure.message}{" "}
                                {failure.retryable
                                  ? "Your answers are still here — try again, or send it as an email instead."
                                  : "Send it as an email instead and it will reach us just the same."}
                              </p>
                              <a
                                href={failure.mailto}
                                className="pg-inline-link"
                              >
                                Send this as an email
                              </a>
                            </div>
                          ) : null}

                          {/* Honeypot: off-screen and skipped by keyboard, so
                              only an automated submission fills it in. */}
                          <input
                            type="text"
                            name="website"
                            value={values.website}
                            onChange={handleChange}
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            style={{
                              position: "absolute",
                              left: "-9999px",
                              width: 1,
                              height: 1,
                              opacity: 0,
                            }}
                          />

                          <div className="pg-form-grid">
                            <div className={fieldClass("name")}>
                              <label htmlFor="ct-name">Your name</label>
                              <input
                                id="ct-name"
                                name="name"
                                type="text"
                                placeholder="John David"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.name && errors.name ? (
                                <p className="pg-error">{errors.name}</p>
                              ) : null}
                            </div>

                            <div className={fieldClass("email")}>
                              <label htmlFor="ct-email">Work email</label>
                              <input
                                id="ct-email"
                                name="email"
                                type="email"
                                placeholder="you@company.com"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.email && errors.email ? (
                                <p className="pg-error">{errors.email}</p>
                              ) : null}
                            </div>

                            <div className={fieldClass("company")}>
                              <label htmlFor="ct-company">Company</label>
                              <input
                                id="ct-company"
                                name="company"
                                type="text"
                                placeholder="Your organisation"
                                value={values.company}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.company && errors.company ? (
                                <p className="pg-error">{errors.company}</p>
                              ) : null}
                            </div>

                            <div className={fieldClass("teamSize")}>
                              <label htmlFor="ct-size">Team size</label>
                              <select
                                id="ct-size"
                                name="teamSize"
                                value={values.teamSize}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Choose one</option>
                                {TEAM_SIZES.map((size) => (
                                  <option key={size} value={size}>
                                    {size}
                                  </option>
                                ))}
                              </select>
                              {touched.teamSize && errors.teamSize ? (
                                <p className="pg-error">{errors.teamSize}</p>
                              ) : null}
                            </div>

                            <div className={`${fieldClass("topic")} is-full`}>
                              <label htmlFor="ct-topic">
                                What is this about?
                              </label>
                              <select
                                id="ct-topic"
                                name="topic"
                                value={values.topic}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Choose a topic</option>
                                {TOPICS.map((topic) => (
                                  <option key={topic} value={topic}>
                                    {topic}
                                  </option>
                                ))}
                              </select>
                              {touched.topic && errors.topic ? (
                                <p className="pg-error">{errors.topic}</p>
                              ) : null}
                            </div>

                            <div className={`${fieldClass("message")} is-full`}>
                              <label htmlFor="ct-message">Your message</label>
                              <textarea
                                id="ct-message"
                                name="message"
                                placeholder="Tell us about your team, how you run sprints today, and what you would like Projam to do for you."
                                value={values.message}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.message && errors.message ? (
                                <p className="pg-error">{errors.message}</p>
                              ) : null}
                            </div>
                          </div>

                          <div className="pg-form-foot">
                            <button
                              type="submit"
                              className="lp-btn lp-btn--primary"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? "Sending…" : "Send message"}
                              <IconArrow />
                            </button>
                            <p>
                              We use what you send only to answer you. No lists,
                              no sharing it on.
                            </p>
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </>
              )}
            </div>

            {/* -------------------------------------------------- channels */}
            <div>
              <div className="pg-channels">
                {CHANNELS.map((channel, i) => (
                  <article
                    className="pg-channel"
                    key={channel.title}
                    data-reveal
                    data-reveal-delay={i * 70}
                  >
                    <div className={`lp-card-icon ${channel.tone}`}>
                      {channel.icon}
                    </div>
                    <div>
                      <h3 className="lp-h3">{channel.title}</h3>
                      <p>{channel.body}</p>
                      {channel.href ? (
                        <a href={channel.href}>{channel.label}</a>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>

              <div style={{ marginTop: 30 }} data-reveal data-reveal-delay="120">
                <h3 className="lp-h3">What happens next</h3>
                <ol className="pg-next">
                  <li>
                    <b>You send the message.</b> It reaches the sales, support
                    or general inbox depending on the topic you picked.
                  </li>
                  <li>
                    <b>We read it properly.</b> A person who works on Projam
                    replies {CONTACT.responseTime} with a real answer, not a
                    holding note.
                  </li>
                  <li>
                    <b>We take it from there.</b> A walkthrough on your own
                    backlog, a plan recommendation, or a fix — whatever the
                    question actually needs.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------- reassurance */}
      <section className="lp-section pg-band">
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              How we work with you
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              What to expect when you get in touch
            </h2>
          </div>

          <div className="lp-grid">
            {REASSURANCE.map((item, i) => (
              <article
                className="lp-card"
                key={item.title}
                data-reveal
                data-reveal-delay={i * 80}
              >
                <div className={`lp-card-icon ${item.tone}`}>{item.icon}</div>
                <h3 className="lp-h3">{item.title}</h3>
                <p className="lp-body">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- cta */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-cta" data-reveal>
            <h2 className="lp-h2">Would you rather just try it?</h2>
            <p>
              Create a project, invite your team and plan your first sprint —
              you do not need to speak to anyone to get started.
            </p>
            <div className="lp-cta-actions">
              <NavLink to="/register" className="lp-btn lp-btn--white">
                Get started free <IconArrow />
              </NavLink>
              <NavLink to="/features" className="lp-btn lp-btn--light">
                Explore the product
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </Box>
  );
};

export default Contact;
